import { Copy, ExternalLink, Send, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { CA, TELEGRAM_URL, X_URL, BUY_LINKS } from "@/config";
import { copyCa, shortCa } from "@/lib/ca";
import { useLanguage } from "@/i18n/LanguageContext";

export const XIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
  </svg>
);

export const OfficialLinks = () => {
  const { t } = useLanguage();
  const SOCIALS = [
    {
      testid: "official-telegram-card",
      icon: Send,
      title: t("official.tgT"),
      desc: t("official.tgD"),
      url: TELEGRAM_URL,
      accent: "text-[#14F195]",
    },
    {
      testid: "official-x-card",
      icon: XIcon,
      title: t("official.xT"),
      desc: t("official.xD"),
      url: X_URL,
      accent: "text-purple-300",
    },
  ];

  const handleCopy = () => {
    if (copyCa()) toast.success(t("hero.toastCopied"));
    else toast(t("hero.toastSoon"));
  };

  return (
  <section id="official-links" className="scroll-mt-24 px-5 py-24 sm:px-8">
    <div className="mx-auto max-w-5xl">
      <Reveal>
        <Eyebrow>{t("official.eyebrow")}</Eyebrow>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display mt-5 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
          {t("official.t1")} <span className="text-flow">{t("official.t2")}</span>
        </h2>
      </Reveal>

      <Reveal delay={0.15}>
        <div
          data-testid="official-links-warning"
          className="mt-8 flex items-start gap-3 rounded-2xl border border-purple-500/30 bg-[#14141E] p-4 sm:items-center sm:p-5"
        >
          <ShieldAlert size={20} className="mt-0.5 shrink-0 text-[#14F195] sm:mt-0" />
          <p className="text-sm leading-relaxed text-gray-300">
            <span className="font-bold text-[#F5F3FF]">{t("official.warnT")}</span>{" "}
            {t("official.warnB")}
          </p>
        </div>
      </Reveal>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        {SOCIALS.map((s, i) => (
          <Reveal key={s.testid} delay={0.1 + i * 0.08}>
            <a
              data-testid={s.testid}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-full items-center justify-between rounded-2xl border border-purple-500/15 bg-[#0E0E16] p-6 transition-all hover:-translate-y-0.5 hover:border-[#14F195]/50"
            >
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#14141E]">
                  <s.icon size={20} className={s.accent} />
                </div>
                <div>
                  <div className="font-display text-base font-bold text-[#F5F3FF]">
                    {s.title}
                  </div>
                  <div className="text-sm text-gray-400">{s.desc}</div>
                </div>
              </div>
              <ExternalLink size={16} className="text-gray-600 transition-colors group-hover:text-[#14F195]" />
            </a>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-4 rounded-2xl border border-purple-500/15 bg-[#0E0E16] p-6">
          <div className="font-display text-xs font-bold uppercase tracking-[0.2em] text-purple-300">
            {t("official.buyOn")}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {BUY_LINKS.map((b) => (
              <a
                key={b.testid}
                data-testid={`official-${b.testid}`}
                href={b.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-purple-500/40 bg-[#14141E] px-5 py-2.5 text-sm font-semibold text-purple-100 transition-colors hover:border-[#14F195]/60 hover:text-[#14F195]"
              >
                {b.name}
                <ExternalLink size={13} className="opacity-60" />
              </a>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-500">
            {t("official.marketNote")}
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.25}>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#14F195]/25 bg-[#0E0E16] p-6">
          <div>
            <div className="font-display text-xs font-bold uppercase tracking-[0.2em] text-purple-300">
              {t("official.caLabel")}
            </div>
            <div
              data-testid="official-ca-value"
              className="font-jb mt-1.5 text-sm text-[#F5F3FF] sm:text-base"
            >
              {CA ? shortCa() : t("official.caSoon")}
            </div>
          </div>
          <button
            data-testid="official-copy-ca-btn"
            onClick={handleCopy}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-transform ${
              CA
                ? "bg-solana-gradient text-white hover:scale-[1.03]"
                : "cursor-not-allowed border border-purple-500/40 bg-[#14141E] text-purple-300"
            }`}
          >
            <Copy size={15} />
            {CA ? t("official.copyCa") : t("official.comingSoon")}
          </button>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
